// Russian grammar abbreviations from card hints ("(скромный, муж.р., Ед.ч.)",
// "(жен. вин.)", "(пр. вр., 3-е лицо)") read out letter by letter or as odd
// fragments, so Car mode spells them out before the text goes to TTS.

const NOT_LETTER_BEFORE = '(?<![А-Яа-яЁё])';
const DOT = '\\.\\s*';

/** `ед. ч.`, `ед.ч.`, `Ед.ч.` — the parts of a dotted abbreviation, spaces optional. */
function dotted(...parts: string[]): string {
    return parts.map(part => `${part}${DOT}`).join('');
}

type Rule = [pattern: string, expansion: string];

// Unambiguous everywhere: compounds and abbreviations no Russian word looks like.
const ANYWHERE: Rule[] = [
    [dotted('ед', 'ч'), 'единственное число'],
    [dotted('мн', 'ч'), 'множественное число'],
    [dotted('(?:муж|м)', 'р'), 'мужской род'],
    [dotted('(?:жен|ж)', 'р'), 'женский род'],
    [dotted('ср', 'р'), 'средний род'],
    [`муж${DOT}род(?![А-Яа-яЁё.])`, 'мужской род'],
    [`жен${DOT}род(?![А-Яа-яЁё.])`, 'женский род'],
    [`ср${DOT}род(?![А-Яа-яЁё.])`, 'средний род'],
    [dotted('им', 'п'), 'именительный падеж'],
    [dotted('род', 'п'), 'родительный падеж'],
    [dotted('дат', 'п'), 'дательный падеж'],
    [dotted('вин', 'п'), 'винительный падеж'],
    [dotted('(?:твор|тв)', 'п'), 'творительный падеж'],
    [dotted('(?:предл|пред|пр)', 'п'), 'предложный падеж'],
    [dotted('зват', 'п'), 'звательный падеж'],
    [dotted('наст', 'вр'), 'настоящее время'],
    [dotted('(?:прош|пр)', 'вр'), 'прошедшее время'],
    [dotted('буд', 'вр'), 'будущее время'],
    [dotted('несов', 'в'), 'несовершенный вид'],
    [dotted('сов', 'в'), 'совершенный вид'],
    [dotted('повел', 'накл'), 'повелительное наклонение'],
    [dotted('сосл', 'накл'), 'сослагательное наклонение'],
    [dotted('сравн', 'ст'), 'сравнительная степень'],
    [dotted('превосх', 'ст'), 'превосходная степень'],
    [dotted('д', 'р'), 'день рождения'],
    [dotted('т', 'е'), 'то есть'],
    [dotted('т', 'д'), 'так далее'],
    [dotted('т', 'п'), 'тому подобное'],
    [`зват${DOT}`, 'звательный падеж '],
    [`напр${DOT}`, 'например '],
];

// Only inside (…) or […], where the grammar notes live: outside them "муж.",
// "род." or "мест." may just end a sentence ("Это мой муж.").
const IN_BRACKETS: Rule[] = [
    [`муж${DOT}`, 'мужской род '],
    [`жен${DOT}`, 'женский род '],
    [`ср${DOT}`, 'средний род '],
    [`им${DOT}`, 'именительный падеж '],
    [`род${DOT}`, 'родительный падеж '],
    [`дат${DOT}`, 'дательный падеж '],
    [`вин${DOT}`, 'винительный падеж '],
    [`(?:твор|тв)${DOT}`, 'творительный падеж '],
    [`(?:предл|пред)${DOT}`, 'предложный падеж '],
    [`ед${DOT}`, 'единственное число '],
    [`мн${DOT}`, 'множественное число '],
    [`наст${DOT}`, 'настоящее время '],
    [`прош${DOT}`, 'прошедшее время '],
    [`буд${DOT}`, 'будущее время '],
    [`несов${DOT}`, 'несовершенный вид '],
    [`сов${DOT}`, 'совершенный вид '],
    [`повел${DOT}`, 'повелительное наклонение '],
    [`накл${DOT}`, 'наклонение '],
    [`инф${DOT}`, 'инфинитив '],
    [`прич${DOT}`, 'причастие '],
    [`деепр${DOT}`, 'деепричастие '],
    [`прил${DOT}`, 'прилагательное '],
    [`сущ${DOT}`, 'существительное '],
    [`гл${DOT}`, 'глагол '],
    [`нар${DOT}`, 'наречие '],
    [`мест${DOT}`, 'местоимение '],
    [`сравн${DOT}`, 'сравнительная степень '],
    [`превосх${DOT}`, 'превосходная степень '],
    [`(\\d)(?:-?е)?\\s*л${DOT}`, '$1-е лицо '],
];

function compile(rules: Rule[]): [RegExp, string][] {
    // The pattern eats the space after the dot, so the expansion puts one back; tidy() trims extras.
    return rules.map(([pattern, expansion]) => [new RegExp(NOT_LETTER_BEFORE + pattern, 'giu'), `${expansion.trimEnd()} `]);
}

const ANYWHERE_RULES = compile(ANYWHERE);
const IN_BRACKETS_RULES = compile(IN_BRACKETS);

function apply(text: string, rules: [RegExp, string][]): string {
    return rules.reduce((result, [pattern, expansion]) => result.replace(pattern, expansion), text);
}

function tidy(text: string): string {
    return text
        .replace(/\s+([,;:)\]])/g, '$1')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

export function expandAbbreviations(text: string): string {
    const everywhere = apply(text, ANYWHERE_RULES);
    const bracketed = everywhere.replace(/([([])([^()[\]]*)([)\]])/g,
        (_match, open: string, inner: string, close: string) => `${open}${tidy(apply(inner, IN_BRACKETS_RULES))}${close}`);
    return tidy(bracketed);
}
